
import { useState, useEffect } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useResponseGeneration } from "@/hooks/useResponseGeneration";
import RegenerateDialog from "./RegenerateDialog";
import EmailHeader from "./EmailHeader";
import InitialEmailView from "./InitialEmailView";
import GeneratedResponseView from "./GeneratedResponseView";
import { type Email } from "@/types/email";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface AIResponseGeneratorProps {
  email: Email;
  selectedAssistant: string;
}

const AIResponseGenerator = ({ email, selectedAssistant }: AIResponseGeneratorProps) => {
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const [regenerateComment, setRegenerateComment] = useState("");
  const [isExpanded, setIsExpanded] = useState(true);

  const {
    response,
    setResponse,
    isGenerating,
    hasGenerated,
    emailSent,
    generationError,
    generateResponse,
    handleSendResponse
  } = useResponseGeneration(email, selectedAssistant);

  useEffect(() => {
    console.log("Email sent status changed:", emailSent);
    if (emailSent) {
      console.log("Collapsing email view due to email being sent");
      setIsExpanded(false);
    }
  }, [emailSent]);

  useEffect(() => {
    console.log("Current expansion state:", isExpanded);
    console.log("Has generated:", hasGenerated);
  }, [isExpanded, hasGenerated]);

  const handleRegenerate = async (comment?: string) => {
    await generateResponse(comment);
    setIsRegenerateDialogOpen(false);
  };

  const handleToggleExpand = () => {
    console.log("Toggle expand clicked, current state:", isExpanded);
    setIsExpanded(!isExpanded);
  };

  const handleSendWithAttachments = (options: { 
    subject: string; 
    cc?: string; 
    bcc?: string; 
    attachments?: File[] 
  }) => {
    handleSendResponse(options);
  };

  const renderErrorMessage = () => {
    if (!generationError) return null;
    
    // Check if it's a rate limit or quota error
    const isRateLimitError = generationError.toLowerCase().includes('rate limit') || 
                             generationError.toLowerCase().includes('quota') ||
                             generationError.toLowerCase().includes('exceeded');
    
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{isRateLimitError ? "OpenAI API Quota Exceeded" : "Error Generating Response"}</AlertTitle>
        <AlertDescription>
          {isRateLimitError ? (
            <>
              Your OpenAI API key has exceeded its quota or rate limit. To fix this:
              <ul className="list-disc pl-5 mt-2">
                <li>Check your OpenAI account billing status</li>
                <li>Add a payment method if needed</li>
                <li>Increase your usage limits in the OpenAI dashboard</li>
              </ul>
            </>
          ) : (
            generationError
          )}
        </AlertDescription>
      </Alert>
    );
  };

  if (!hasGenerated) {
    return (
      <>
        {renderErrorMessage()}
        <InitialEmailView
          email={email}
          isGenerating={isGenerating}
          selectedAssistant={selectedAssistant}
          onGenerate={() => generateResponse()}
        />
      </>
    );
  }

  return (
    <Collapsible 
      open={isExpanded} 
      onOpenChange={setIsExpanded}
    >
      <div className="space-y-4">
        {renderErrorMessage()}
        
        <CollapsibleTrigger asChild>
          <EmailHeader
            subject={email.subject}
            emailSent={emailSent}
            isExpanded={isExpanded}
            hasGenerated={hasGenerated}
            onClick={handleToggleExpand}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <GeneratedResponseView
            email={email}
            response={response}
            isGenerating={isGenerating}
            emailSent={emailSent}
            onResponseChange={setResponse}
            onRegenerate={() => setIsRegenerateDialogOpen(true)}
            onSend={handleSendWithAttachments}
          />
        </CollapsibleContent>
      </div>

      <RegenerateDialog
        isOpen={isRegenerateDialogOpen}
        onClose={() => setIsRegenerateDialogOpen(false)}
        comment={regenerateComment}
        onCommentChange={setRegenerateComment}
        onRegenerate={() => handleRegenerate(regenerateComment)}
        isGenerating={isGenerating}
      />
    </Collapsible>
  );
};

export default AIResponseGenerator;
